import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  Badge,
  Button,
  Collapse,
  Descriptions,
  Input,
  Radio,
  Spin,
  Tag,
} from "antd";
import moment from "moment";
import { Report } from "notiflix";

import Navbar from "../../../atoms/navbar";
import Footer from "../../../atoms/footer";
import ApprovalActions from "../../../atoms/approval-actions";
import ApprovalInfo from "../../../atoms/approval-info";

import "../create/styles.css";
import "./styles.css";

import HeaderFarms from "../../../utils/header_farms";
import { requestToken } from "../../../auth/AuthServices";
import API from "../../../services";
import { normalizeLevel } from "../utils";
import { NON_STAFF_NAMES } from "../../../constants/pa.constant";

const { TextArea } = Input;

const statusColorMap = {
  "Waiting Approval": "#FF9900",
  Approved: "blue",
  Reject: "red",
  Rejected: "red",
  Cancel: "gray",
  Closed: "green",
};

const getStatusColor = (status) => statusColorMap[status] || "#FF9900";

const slugifyLabel = (label = "") =>
  (label || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

const formatFileSize = (size) => {
  if (!size) return "";
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(size / 1024).toFixed(1)} KB`;
};

const buildPerformanceSelectionMap = (items = []) => {
  const map = {};
  (items || []).forEach((item) => {
    map[item.aspect_code] = item.selected_value ?? null;
  });
  return map;
};

const buildCompetencyScoreMap = (items = []) => {
  const map = {};
  (items || []).forEach((item) => {
    const key = `${item.competency_code}_${item.statement_no}`;
    map[key] = item.score;
  });
  return map;
};

const buildPerformanceCommentMap = (items = []) => {
  const map = {};
  (items || []).forEach((item) => {
    if (item?.aspect_code) {
      map[item.aspect_code] = item.comment || "";
    }
  });
  return map;
};

const buildCompetencyCommentMap = (items = []) => {
  const map = {};
  (items || []).forEach((item) => {
    if (item?.statement_no !== undefined) {
      const normalizedCode =
        item.competency_code && item.competency_code.includes("_")
          ? item.competency_code
          : slugifyLabel(item.competency_label) || item.competency_code;
      if (normalizedCode) {
        const key = `${normalizedCode}_${item.statement_no}`;
        map[key] = item.comment || "";
      }
    }
  });
  return map;
};

const buildTaskCommentMap = (items = []) => {
  const map = {};
  (items || []).forEach((item) => {
    if (item.order_no !== undefined) {
      const key = `${item.year || ""}-${item.order_no}`;
      map[key] = item.comment || "";
    }
  });
  return map;
};

const groupTasksByYear = (tasks = [], year) => {
  const period = Number(year) || new Date().getFullYear();
  const current = (tasks || []).filter(
    (task) => Number(task.year) === period
  );
  const future = (tasks || []).filter(
    (task) => Number(task.year) === period + 1
  );

  return { current, future };
};

const normalizeTaskList = (tasks = []) =>
  (tasks || [])
    .map((task) => ({
      order_no: task.order_no ?? 0,
      year: task.year ?? null,
      description: (task.description || "").trim(),
      kpi_indicator: (task.kpi_indicator || "").trim(),
      achievement_date: task.achievement_date
        ? moment(task.achievement_date).format("YYYY-MM-DD")
        : null,
      result_comment: (task.result_comment || "").trim(),
      note: (task.note || "").trim(),
    }))
    .sort((a, b) => a.order_no - b.order_no);

const sortTasksForView = (tasks = []) =>
  [...(tasks || [])].sort(
    (a, b) => (a.order_no || 0) - (b.order_no || 0)
  );

const getTaskDiffSet = (snapshot = [], current = []) => {
  const normalizedSnapshot = normalizeTaskList(snapshot);
  const normalizedCurrent = normalizeTaskList(current);
  const maxLength = Math.max(
    normalizedSnapshot.length,
    normalizedCurrent.length
  );
  const diffSet = new Set();

  for (let index = 0; index < maxLength; index += 1) {
    const snap = normalizedSnapshot[index] || {};
    const curr = normalizedCurrent[index] || {};
    if (
      (snap.year ?? null) !== (curr.year ?? null) ||
      (snap.description || "") !== (curr.description || "") ||
      (snap.kpi_indicator || "") !== (curr.kpi_indicator || "") ||
      (snap.achievement_date || null) !== (curr.achievement_date || null) ||
      (snap.result_comment || "") !== (curr.result_comment || "") ||
      (snap.note || "") !== (curr.note || "")
    ) {
      diffSet.add(index);
    }
  }

  return diffSet;
};

const isTaskListDifferent = (snapshot = [], current = []) =>
  getTaskDiffSet(snapshot, current).size > 0;

function PATransactionDetail() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [performanceMaster, setPerformanceMaster] = useState([]);
  const [competencyMaster, setCompetencyMaster] = useState([]);
  const approvalData = data?.approval_process_id?.data || null;
  const sessionInfo = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("session_farms") || "{}");
    } catch (error) {
      return {};
    }
  }, []);


  const levelForMaster = useMemo(
    () => normalizeLevel(data?.level || ""),
    [data?.level]
  );

  const isNonStaff = NON_STAFF_NAMES.includes(levelForMaster);

  const collapseDefaultKeys = useMemo(() => {
    const keys = ["detail", "performance", "competency", "tasks"];
    if (approvalData) {
      keys.splice(1, 0, "approval");
    }
    return keys;
  }, [approvalData]);

const mapApprovalProcess = (source) => {
  if (!source) return null;
  if (source.data) return source;
  return { data: source };
};

useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const token = await requestToken();
        const response = await API.GetPATransaction(
          HeaderFarms(token),
          recordId
        );
        const detail = response?.data || response;

        const normalized = { ...detail };
        const approvalSource =
          normalized.approval_process_id || normalized.approval_process;
        normalized.approval_process_id = mapApprovalProcess(approvalSource);

        setData(normalized);

        if (normalized?.level) {
          const params = {
            year: normalized.period_year || new Date().getFullYear(),
            level: normalizeLevel(normalized.level),
          };

          const [perfResp, compResp] = await Promise.all([
            API.ListPAMasterPerformance(HeaderFarms(token), params),
            API.ListPAMasterCompetencies(HeaderFarms(token), params),
          ]);

          setPerformanceMaster(perfResp.data?.[0]?.aspects || []);
          setCompetencyMaster(compResp.data?.[0]?.competencies || []);
        }
      } catch (error) {
        console.error("GetPATransaction error:", error);
        Report.warning(
          "Data tidak ditemukan",
          "Record Performance Appraisal tidak tersedia.",
          "Kembali",
          () => navigate("/pa/history")
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [recordId, navigate]);

  const effectiveApproverAdjustments = useMemo(() => {
    const status = approvalData?.status || data?.status;
    if (status === "Approved" && !data?.approver_adjustments) {
      return {
        performances: data?.performances || [],
        competencies: data?.competencies || [],
        tasks: data?.tasks || [],
        snapshot_at: approvalData?.updatedAt,
        snapshot_by: approvalData?.updated_by,
        comments: {
          performances: [],
          competencies: [],
          tasks: []
        },
      };
    }
    return data?.approver_adjustments || null;
  }, [data?.approver_adjustments, data?.performances, data?.competencies, data?.tasks, approvalData?.status, approvalData?.updatedAt, approvalData?.updated_by, data?.status]);

  const performanceMap = useMemo(
    () => buildPerformanceSelectionMap(data?.performances || []),
    [data?.performances]
  );

  const snapshotPerformanceMap = useMemo(
    () =>
      buildPerformanceSelectionMap(
        effectiveApproverAdjustments?.performances || []
      ),
    [effectiveApproverAdjustments?.performances]
  );

  const competencyScoreMap = useMemo(
    () => buildCompetencyScoreMap(data?.competencies || []),
    [data?.competencies]
  );

  const snapshotCompetencyScoreMap = useMemo(
    () =>
      buildCompetencyScoreMap(
        effectiveApproverAdjustments?.competencies || []
      ),
    [effectiveApproverAdjustments?.competencies]
  );

  const performanceCommentMap = useMemo(
    () =>
      buildPerformanceCommentMap(
        effectiveApproverAdjustments?.comments?.performances || []
      ),
    [effectiveApproverAdjustments?.comments?.performances]
  );
  const competencyCommentMap = useMemo(
    () =>
      buildCompetencyCommentMap(
        effectiveApproverAdjustments?.comments?.competencies || []
      ),
    [effectiveApproverAdjustments?.comments?.competencies]
  );
  const taskCommentMap = useMemo(
    () =>
      buildTaskCommentMap(
        effectiveApproverAdjustments?.comments?.tasks || []
      ),
    [effectiveApproverAdjustments?.comments?.tasks]
  );

  const tasksByYear = useMemo(
    () => groupTasksByYear(data?.tasks || [], data?.period_year),
    [data?.tasks, data?.period_year]
  );

  const snapshotTasksByYear = useMemo(
    () =>
      groupTasksByYear(
        effectiveApproverAdjustments?.tasks || [],
        data?.period_year
      ),
    [effectiveApproverAdjustments?.tasks, data?.period_year]
  );

  const tasksWithComments = useMemo(() => {
    const annotate = (tasks = []) =>
      tasks.map((task, index) => {
        const key = `${task.year || ""}-${task.order_no || index + 1}`;
        return {
          ...task,
          approver_comment:
            taskCommentMap[key] ||
            taskCommentMap[`${task.year || ""}-${index + 1}`] ||
            "",
        };
      });
    return {
      current: annotate(tasksByYear.current),
      future: annotate(tasksByYear.future),
    };
  }, [tasksByYear, taskCommentMap]);

  const hasApproverSnapshot = Boolean(
    effectiveApproverAdjustments?.snapshot_at
  );
  const snapshotMeta = effectiveApproverAdjustments;
  const snapshotBy = snapshotMeta?.snapshot_by;
  const snapshotAt = snapshotMeta?.snapshot_at
    ? moment(snapshotMeta.snapshot_at).format("DD MMM YYYY HH:mm")
    : null;

  const renderTaskItems = (
    title,
    tasks,
    keyPrefix,
    changedSet = new Set(),
    showApproverComment = false
  ) => {
    if (!tasks.length) {
      return <p className="text-muted mb-0">Belum ada data tugas.</p>;
    }

    return tasks.map((task, index) => (
      <div
        key={`${keyPrefix}-${index}`}
        className={`pa-task-card ${
          changedSet.has(index) ? "pa-task-card-changed" : ""
        }`}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <p className="mb-0 fw-bold">Tugas #{index + 1}</p>
          <span className="pa-task-year-badge">{task.year}</span>
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="pa-field-label">Deskripsi</label>
            <div className="pa-task-value">{task.description || "-"}</div>
          </div>
          <div className="col-md-6">
            <label className="pa-field-label">Indikator / KPI</label>
            <div className="pa-task-value">{task.kpi_indicator || "-"}</div>
          </div>
          <div className="col-md-4 col-sm-6">
            <label className="pa-field-label">Tanggal Pencapaian</label>
            <div className="pa-task-value">
              {task.achievement_date
                ? moment(task.achievement_date).format("DD MMM YYYY")
                : "-"}
            </div>
          </div>
          <div className="col-md-4 col-sm-6">
            <label className="pa-field-label">Tahun</label>
            <div className="pa-task-value text-center">
              {task.year || "-"}
            </div>
          </div>
          <div className="col-12">
            <label className="pa-field-label">
              {title.includes("Terakhir") ? "Hasil / Komentar" : "Catatan"}
            </label>
            <div className="pa-task-value">
              {title.includes("Terakhir")
                ? task.result_comment || "-"
                : task.note || "-"}
            </div>
          </div>
          {title.includes("Terakhir") && (
            <div className="col-12">
              <label className="pa-field-label">Evidence</label>
              {task.evidence_file_url ? (
                <div className="pa-task-value d-flex flex-wrap align-items-center gap-2">
                  <a
                    href={task.evidence_file_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {task.evidence_file_name || "Lihat Evidence"}
                  </a>
                  {task.evidence_file_size ? (
                    <span className="text-muted">
                      ({formatFileSize(task.evidence_file_size)})
                    </span>
                  ) : null}
                </div>
              ) : (
                <div className="pa-task-value">-</div>
              )}
            </div>
          )}
          {showApproverComment && (
            <div className="col-12">
              <label className="pa-field-label">Komentar Atasan Terkait</label>
              {(() => {
                const commentKey = `${task.year || ""}-${task.order_no || index + 1}`;
                const commentValue =
                  task.approver_comment ||
                  taskCommentMap[commentKey] ||
                  taskCommentMap[`${task.year || ""}-${index + 1}`] ||
                  "";
                return (
                  <TextArea
                    rows={2}
                    value={commentValue}
                    placeholder="-"
                    disabled
                    className="pa-task-comment-input"
                  />
                );
              })()}
            </div>
          )}
        </div>
      </div>
    ));
  };

  const renderTaskSection = (title, finalTasks, snapshotTasks = []) => {
    const orderedSnapshot = sortTasksForView(snapshotTasks);
    const orderedFinal = sortTasksForView(finalTasks);
    if (!hasApproverSnapshot) {
      return (
        <div className="pa-card">
          <p className="pa-section-title mb-2">{title}</p>
          {renderTaskItems(
            title,
            orderedFinal,
            `${title}-final`,
            new Set(),
            true
          )}
        </div>
      );
    }

    const diffSet = getTaskDiffSet(orderedSnapshot, orderedFinal);
    return (
      <div className="pa-card">
        <p className="pa-section-title mb-2">{title}</p>
        <div className="pa-compare-wrapper">
          <div className="pa-compare-block">
            <p className="pa-compare-title">Employee</p>
            {renderTaskItems(
              title,
              orderedSnapshot,
              `${title}-employee`,
              diffSet,
              false
            )}
          </div>
          <div className="pa-compare-block">
            <p className="pa-compare-title">Final / Approver</p>
            {renderTaskItems(
              title,
              orderedFinal,
              `${title}-approver`,
              diffSet,
              true
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStatusTag = () => {
    const status =
      approvalData?.status ||
      data?.status ||
      "Waiting Approval";
    return <Tag color={getStatusColor(status)}>{status}</Tag>;
  };

  const canEditRecord = useMemo(() => {
    if (!approvalData || !sessionInfo?.employee_id) return false;
    if (approvalData.status === "Reject") return false;

    const detailList = approvalData.detail || [];
    const isDirectApprover = detailList.some(
      (item) =>
        (item.approver?.employee_id || item.approver_id) === sessionInfo.employee_id
    );
    const isDeputyApprover = detailList.some(
      (item) => item.deputy_approver === sessionInfo.employee_id
    );
    const formSetting = approvalData.form_id?.form_setting?.[0] || {};
    const isPIC =
      Array.isArray(formSetting.pic) &&
      formSetting.pic.includes(sessionInfo.employee_id) &&
      !!formSetting.pic_approval_refer &&
      detailList.some(
        (item) =>
          (item.approver?.nik || item.approver_id) ===
          formSetting.pic_approval_refer
      );

    return isDirectApprover || isDeputyApprover || isPIC;
  }, [approvalData, sessionInfo?.employee_id]);

  const showEditButton =
    canEditRecord &&
    Boolean(data?.id_record || data?.record_id);

  return (
    <Fragment>
      <Navbar
        title="Detail"
        back={true}
        subtitle="Performance Appraisal"
        navigate="/pa/history"
      />
      <div className="container white-back-cr pa-view-container" style={{ minHeight: 500 }}>
        <div className="container mt-3 p-3">
          {loading ? (
            <div className="text-center my-5">
              <Spin size="large" />
            </div>
          ) : data ? (
            <>
              {hasApproverSnapshot && (
                <div className="pa-snapshot-info mb-3">
                  <p className="fw-semibold mb-1">Perbandingan data</p>
                  <p className="mb-0">
                    Nilai awal karyawan disimpan pada{" "}
                    {snapshotAt || "-"}
                    {snapshotBy ? ` oleh ${snapshotBy}` : ""}. Perubahan oleh
                    approver ditandai dengan highlight merah.
                  </p>
                </div>
              )}
              {showEditButton && (
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    type="primary"
                    onClick={() => navigate(`/pa/edit/${recordId}`)}
                  >
                    Edit Penilaian
                  </Button>
                </div>
              )}
              <Collapse
                defaultActiveKey={collapseDefaultKeys}
                expandIconPosition="end"
                className="pa-collapse"
              >
              <Collapse.Panel header={<b>Performance Appraisal Detail</b>} key="detail">
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Record ID">
                    {data.record_id || data.id_record || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Nama Karyawan">
                    {data.fullname}
                  </Descriptions.Item>
                  <Descriptions.Item label="Employee ID">
                    {data.employee_id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Jabatan">
                    {data.position}
                  </Descriptions.Item>
                  <Descriptions.Item label="Level">
                    {data.level}
                  </Descriptions.Item>
                  <Descriptions.Item label="Departemen">
                    {data.department}
                  </Descriptions.Item>
                  <Descriptions.Item label="Periode">
                    {data.period_year} (
                    {moment(data.period_start).format("DD MMM")} -{" "}
                    {moment(data.period_end).format("DD MMM YYYY")})
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    {renderStatusTag()}
                  </Descriptions.Item>
                </Descriptions>
              </Collapse.Panel>

              {data.approval_process_id?.data && (
                <Collapse.Panel header={<b>Approval Info & Comments</b>} key="approval">
                  <ApprovalInfo documentData={data} usePanel={false} />
                </Collapse.Panel>
              )}

              <Collapse.Panel header={<b>Aspek Kinerja</b>} key="performance">
                {performanceMaster.length === 0 ? (
                  <p className="text-muted mb-0">
                    Master performance tidak ditemukan.
                  </p>
                ) : (
                  performanceMaster.map((aspect) => {
                    const employeeSelection =
                      snapshotPerformanceMap[aspect.aspect_code];
                    const finalSelection =
                      performanceMap[aspect.aspect_code];
                    const changed =
                      hasApproverSnapshot &&
                      employeeSelection !== finalSelection;

                    const renderSelection = (value) => (
                      <Radio.Group
                        value={value}
                        onChange={() => {}}
                        className="pa-performance-options pa-radio-view"
                      >
                        {aspect.statements.map((statement) => (
                          <Radio
                            key={`${aspect.aspect_code}-${statement.order}`}
                            value={statement.order}
                            className="pa-performance-option"
                          >
                            <div className="pa-radio-label">
                              <span className="pa-radio-order">
                                {statement.order}.
                              </span>
                              <span>{statement.description}</span>
                            </div>
                          </Radio>
                        ))}
                      </Radio.Group>
                    );

                    return (
                      <div key={aspect.aspect_code} className="pa-card mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                          <div>
                            <p className="pa-section-title mb-0">
                              {aspect.aspect_label}
                            </p>
                            <small className="text-muted">
                              {aspect.aspect_code}
                            </small>
                          </div>
                        </div>
                        {hasApproverSnapshot ? (
                          <div className="pa-compare-wrapper">
                            <div
                              className={`pa-compare-block ${
                                changed ? "pa-changed" : ""
                              }`}
                            >
                              <p className="pa-compare-title">Employee</p>
                              {renderSelection(employeeSelection)}
                            </div>
                            <div
                              className={`pa-compare-block ${
                                changed ? "pa-changed" : ""
                              }`}
                            >
                              <p className="pa-compare-title">
                                Final / Approver
                              </p>
                              {renderSelection(finalSelection)}
                              {performanceCommentMap[aspect.aspect_code] && (
                                <div className="pa-approver-comment mt-2">
                                  <label>Komentar Atasan Terkait</label>
                                  <p className="mb-0">
                                    {performanceCommentMap[aspect.aspect_code]}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <>
                            {renderSelection(finalSelection)}
                            {performanceCommentMap[aspect.aspect_code] && (
                              <div className="pa-approver-comment mt-2">
                                <label>Komentar Atasan Terkait</label>
                                <p className="mb-0">
                                  {performanceCommentMap[aspect.aspect_code]}
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })
                )}
              </Collapse.Panel>

              <Collapse.Panel header={<b>Kompetensi</b>} key="competency">
                {competencyMaster.length === 0 ? (
                  <p className="text-muted mb-0">
                    Master competency tidak ditemukan.
                  </p>
                ) : (
                  competencyMaster.map((comp) => (
                    <div key={comp.competency_code} className="pa-card mb-3">
                      <p className="pa-section-title mb-1">
                        {comp.competency_label}
                      </p>
                      {comp.statements.map((stmt) => {
                        const key = `${comp.competency_code}_${stmt.statement_no}`;
                        const labelKey = `${slugifyLabel(comp.competency_label)}_${stmt.statement_no}`;
                        const employeeScore = snapshotCompetencyScoreMap[key];
                        const finalScore = competencyScoreMap[key];
                        const commentValue =
                          competencyCommentMap[key] ??
                          competencyCommentMap[labelKey] ??
                          "";
                        const changed =
                          hasApproverSnapshot &&
                          employeeScore !== finalScore;

                        const renderRadio = (value) => (
                          <Radio.Group
                            value={value}
                            optionType="default"
                            buttonStyle="solid"
                            size="small"
                            onChange={() => {}}
                            className="pa-competency-options pa-radio-view mt-3"
                          >
                            <Radio.Button value={1}>
                              Tidak Setuju
                            </Radio.Button>
                            <Radio.Button value={2}>Cukup</Radio.Button>
                            <Radio.Button value={3}>Setuju</Radio.Button>
                          </Radio.Group>
                        );

                        return (
                          <div
                            key={key}
                            className="pa-competency-item pa-competency-view"
                          >
                            <p className="mb-2 fw-medium">
                              {stmt.statement_no}. {stmt.description}
                            </p>
                            {hasApproverSnapshot ? (
                              <div className="pa-compare-wrapper slim">
                                <div
                                  className={`pa-compare-block ${
                                    changed ? "pa-changed" : ""
                                  }`}
                                >
                                  <p className="pa-compare-title mb-2">
                                    Employee
                                  </p>
                                  {renderRadio(employeeScore)}
                                </div>
                                <div
                                  className={`pa-compare-block ${
                                    changed ? "pa-changed" : ""
                                  }`}
                                >
                                  <p className="pa-compare-title mb-2">
                                    Final / Approver
                                  </p>
                                  {renderRadio(finalScore)}
                                  {commentValue && (
                                    <div className="pa-approver-comment mt-2">
                                      <label>Komentar Atasan Terkait</label>
                                      <p className="mb-0">
                                        {commentValue}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <>
                                {renderRadio(finalScore)}
                                {commentValue && (
                                  <div className="pa-approver-comment mt-2">
                                    <label>Komentar Atasan Terkait</label>
                                    <p className="mb-0">
                                      {commentValue}
                                    </p>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
              </Collapse.Panel>

              {/* All job level except Non Staff */}
              {!isNonStaff && (
                <Collapse.Panel header={<b>Tugas</b>} key="tasks">
                  {renderTaskSection(
                    "12 Bulan Terakhir",
                    tasksWithComments.current,
                    snapshotTasksByYear.current
                  )}
                  {renderTaskSection(
                    "12 Bulan Berikutnya",
                    tasksWithComments.future,
                    snapshotTasksByYear.future
                  )}
                </Collapse.Panel>
              )}
            </Collapse>
            </>
          ) : null}
        </div>
        {data && <ApprovalActions mainData={data} />}
      </div>
      <Footer />
    </Fragment>
  );
}

export default PATransactionDetail;
